"use client";

import { Button } from "@/components/ui/button";
import { DeleteIcon } from "@/components/ui/delete";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FolderPlusIcon } from "@/components/ui/folder-plus";
import type { FileManagerTreeNode } from "@/hooks/use-file-manager-tree";
import { cn } from "@/lib/utils";
import { FolderIcon, MoreHorizontalIcon } from "lucide-react";
import { countStatuses, pluralise, type KbDragPayload } from "./kb-utils";

type CollectionGridProps = {
  collections: FileManagerTreeNode[];
  onOpen: (collection: FileManagerTreeNode) => void;
  onCreate: () => void;
  canCreate: boolean;
  onStartChat: (collection: FileManagerTreeNode) => void;
  onRename: (collection: FileManagerTreeNode) => void;
  onChangeTeam: (collection: FileManagerTreeNode) => void;
  onDelete: (collection: FileManagerTreeNode) => void;
  drag: KbDragPayload | null;
  dropTargetId: string | null;
  onDropTargetChange: (nodeId: string | null) => void;
  onDropFiles: (collection: FileManagerTreeNode) => void;
};

/** Folder-style cards for a team's collections, ending with a "New collection" card. */
export function CollectionGrid({
  collections,
  onOpen,
  onCreate,
  canCreate,
  onStartChat,
  onRename,
  onChangeTeam,
  onDelete,
  drag,
  dropTargetId,
  onDropTargetChange,
  onDropFiles,
}: CollectionGridProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {collections.map((collection) => {
        const isEmpty = collection.fileCount === 0;
        const failedCount = countStatuses(collection.children ?? []).failed;
        // Within-team collection moves are immediate, so any in-team drag qualifies.
        const canDrop = !!drag && drag.teamId === collection.teamId;
        const isDropTarget = canDrop && dropTargetId === collection.id;

        return (
          <div
            key={collection.id}
            className={cn(
              "group relative rounded-2xl border bg-card transition-colors",
              "border-border hover:bg-accent/50",
              canDrop && !isDropTarget && "border-dashed border-primary/50",
              isDropTarget && "border-primary bg-primary/15 ring-2 ring-primary"
            )}
            onDragOver={(event) => {
              if (!canDrop) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              onDropTargetChange(collection.id);
            }}
            onDragLeave={(event) => {
              if (!canDrop) return;
              if (event.currentTarget.contains(event.relatedTarget as Node)) return;
              onDropTargetChange(null);
            }}
            onDrop={(event) => {
              if (!canDrop) return;
              event.preventDefault();
              onDropFiles(collection);
            }}
          >
            <button
              type="button"
              onClick={() => onOpen(collection)}
              className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 pr-12 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <span
                className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground"
                aria-hidden
              >
                <FolderIcon className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">
                  {collection.name}
                </span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground tabular-nums">
                  {isDropTarget ? (
                    "Drop to move here"
                  ) : isEmpty ? (
                    "No files yet"
                  ) : (
                    <>
                      {pluralise(collection.fileCount, "file")}
                      {failedCount > 0 ? (
                        <span className="text-destructive"> · {failedCount} failed</span>
                      ) : null}
                    </>
                  )}
                </span>
              </span>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`More actions for ${collection.name}`}
                  className="absolute right-2 top-1/2 size-8 -translate-y-1/2 text-muted-foreground hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100 sm:data-[state=open]:opacity-100"
                >
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onOpen(collection)}>Open</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onStartChat(collection)} disabled={isEmpty}>
                  Start chat
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onRename(collection)}>Rename</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onChangeTeam(collection)}>
                  Change team
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => onDelete(collection)}
                >
                  <DeleteIcon className="size-4" size={16} />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })}

      <button
        type="button"
        onClick={onCreate}
        disabled={!canCreate}
        className={cn(
          "flex min-h-[68px] items-center justify-center gap-2 rounded-2xl border border-dashed border-border px-4 py-3.5 text-sm text-muted-foreground transition-colors",
          "hover:bg-accent/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-50",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        )}
      >
        <FolderPlusIcon className="size-4" size={16} />
        New collection
      </button>
    </div>
  );
}
